import React, { createContext, useContext, useState, useEffect } from "react";
import consts from "../../main/consts";
import { remoteConfigMock } from "../../utils/remote-config-mock";
import { devolutionMatchers } from "./devolutionMatchers";
import { remoteConfigTypes } from "./types";
import navigationConfig from "../../main/navigationConfig";

const RemoteConfigContext = createContext({});

export const RemoteConfigProvider = ({ children }) => {
    const [remoteConfig, setRemoteConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!consts.REMOTE_CONFIG_URL) {
            setLoading(false);
            return;
        }

        fetch(consts.REMOTE_CONFIG_URL)
            .then((res) => {
                if (!res.ok) throw new Error("Remote config request failed: " + res.status);
                return res.json();
            })
            .then((data) => {
                setRemoteConfig(data.maestro || remoteConfigMock);
            })
            .catch((err) => {
                setRemoteConfig(remoteConfigMock);
                setError(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const getGlobalConfig = (key, defaultValue) => {
        if (!remoteConfig || !remoteConfig.globalConfig) return defaultValue !== undefined ? defaultValue : undefined;
        return remoteConfig.globalConfig[key] != null ? remoteConfig.globalConfig[key] : (defaultValue !== undefined ? defaultValue : undefined);
    };

    const isModuleEnabled = (moduleName) => {
        if (!remoteConfig) return false;
        var mod = remoteConfig.modules && remoteConfig.modules[moduleName];
        return mod ? mod.enabled === true : false;
    };

    const isPageEnabled = (moduleName, pageName) => {
        if (!remoteConfig) return false;
        var mod = remoteConfig.modules && remoteConfig.modules[moduleName];
        if (!mod || !mod.pages) return false;
        var page = mod.pages[pageName];
        return page ? page.enabled === true : false;
    };

    const getToggleFeature = (moduleName, pageName, featureName) => {
        if (!remoteConfig) return false;
        try {
            var toggleFeatures = remoteConfig.modules[moduleName].pages[pageName].toggleFeatures;
            if (toggleFeatures) return toggleFeatures[featureName] != null ? toggleFeatures[featureName] : false;
            return false;
        } catch (e) {
            return false;
        }
    };

    const isMenuEnabled = (menuId) => {
        const menus = remoteConfig?.globalConfig?.[remoteConfigTypes.GLOBAL_CONFIG.MENUS_HABILITADOS];
        const value = menus?.[menuId];
        if (value != null) return value !== false;

        const menu = navigationConfig.find((nav) => nav.id === menuId);
        return menu ? menu.enabledByDefault !== false : true;
    };

    const getDaysToDevolution = (params = {}) => {
        const daysConfig = remoteConfig?.globalConfig?.daysToDevolution;
        const matcher = devolutionMatchers.find(m => m.match(params));
        const key = matcher ? matcher.key : 'normal';
        return daysConfig?.[key] ?? consts.DAYS_TO_DEVOLUTION;
    };

    return (
        <RemoteConfigContext.Provider
            value={{
                remoteConfig,
                loading,
                error,
                isModuleEnabled,
                isPageEnabled,
                getToggleFeature,
                getGlobalConfig,
                getDaysToDevolution,
                isMenuEnabled,
            }}
        >
            {children}
        </RemoteConfigContext.Provider>
    );
};

export const useRemoteConfig = () => {
    return useContext(RemoteConfigContext);
};

export const withRemoteConfig = (WrappedComponent) => {
    return React.forwardRef((props, ref) => {
        const remoteConfig = useRemoteConfig();
        return <WrappedComponent {...props} ref={ref} remoteConfig={remoteConfig} />;
    });
};

export default RemoteConfigContext;
